import CONSTS from 'constants/index.js';
const { QUICK_FILTERS } = CONSTS.CONVERSATION_FILTERS;

/**
 * @typedef {{[key: string]: any}} Conversation
 */

/**
 * A function that takes a conversation and returns a boolean indicating whether it matches
 * @typedef {(conversation: Conversation) => Boolean} Matcher
 */

/**
 * Takes an array of Matchers and returns a new Matcher that returns true if they all pass against the conversation
 * @param {Array<Matcher>} matchers
 * @returns {Matcher} all matcher
 */
const all = (matchers) => (conversation) =>
  matchers.every((matcher) => matcher(conversation));

/**
 * Takes an array of Matchers and returns a new Matcher that returns true if any pass against the conversation
 * @param {Array<Matcher>} matchers
 * @returns {Matcher} either matcher
 */
const either = (matchers) => (conversation) =>
  matchers.some((matcher) => matcher(conversation));

/**
 * Takes a Matcher and returns returns a new Matcher that negates its result
 * @param {Matcher} matcher
 * @returns {Matcher} negated matcher
 */
const negate = (matcher) => (conversation) => !matcher(conversation);

/**
 * Takes a status and returns a Matcher which returns true if the conversation has that status
 * @param {string} status
 * @returns {Matcher} status matcher
 */
const statusIs = (status) => (conversation) => conversation.status === status;

/**
 * Takes an array of statuses and returns a Matcher which returns true if the conversation has one of them
 * @param {Array<string>} statuses
 * @returns {Matcher} status matcher
 */
const statusIsOneOf = (statuses) => (conversation) =>
  statuses.includes(conversation.status);

/**
 * Takes an array of statuses and returns a Matcher which returs true if the conversation doesn't have any of them
 * @param {Array<string>} statuses
 * @returns {Matcher} status matcher
 */
const statusIsNotOneOf = (statuses) => negate(statusIsOneOf(statuses));

/**
 * Takes an array of friendly statuses and returns a Matcher which returns true if the conversation has one of them
 * a friendly status is one that an Agent would understand as opposed to the actual saved status.
 * @param {Array<'open'|'closed'|'sent'>} friendlyStatuses
 * @returns {Matcher} status matcher
 */
const friendlyStatusIsOneOf = (friendlyStatuses) => {
  const statusMap = {
    open: ['new', 'open'],
    closed: ['closed'],
    sent: ['no reply', 'channel switch'],
  };
  const normalisedStatuses = friendlyStatuses.reduce(
    (normalised, friendlyStatus) => {
      const replacements = statusMap[friendlyStatus];
      return [...normalised, ...replacements];
    },
    []
  );
  return statusIsOneOf(normalisedStatuses);
};

/**
 * A Matcher that returns true if the conversation is assigned to a Bot
 * @param {Conversation} conversation
 * @returns {Boolean} result
 */
const isAssignedToAnyBot = (conversation) => Boolean(conversation.bot_id);

/**
 * A Matcher that returns true if the conversation has been assigned to a Bot
 * @param {Conversation} conversation
 * @returns {Boolean} result
 */
const hasBeenAssignedToAnyBot = (conversation) =>
  Boolean(conversation.last_bot_id);

/**
 * A Matcher that returns true if the conversation is not assigned to a Bot
 * @param {Conversation} conversation
 * @returns {Boolean} result
 */
const isNotAssignedToAnyBot = negate(isAssignedToAnyBot);

/**
 * Takes an agent id and returns a Matcher which returns true if the agent is assigned to the conversation
 * @param {Number} agentId
 * @returns {Matcher} assignment matcher
 */
const isAssignedToAgent = (agentId) => (conversation) =>
  conversation.all_assigned_agent_ids.includes(agentId);

/**
 * Takes an agent id and returns a Matcher which returns true if the agent is directly assigned to the conversation
 * @param {Number} agentId
 * @returns {Matcher} assignment matcher
 */
const isAssignedDirectlyToAgent = (agentId) => (conversation) =>
  conversation.assigned_agent_ids.includes(agentId);

/**
 * Teaks a team id and returns a Matcher which returns true if the team is directly assigned to the conversation
 * @param {Number} teamId
 * @returns {Matcher} assignment matcher
 */
const isAssignedDirectlyToTeam = (teamId) => (conversation) =>
  conversation.assigned_team_ids.includes(teamId);

/**
 * Takes a bot id and returns a Matcher which returns true if the bot is currently handling the conversation
 * @param {Number} botId
 * @returns {Matcher} bot assignment matcher
 */
const isAssignedToBot = (botId) => (conversation) =>
  conversation.bot_id === botId;

/**
 * Takes a channel id and returns a Matcher which returns true if it is the last used channel in the conversation
 * @param {Number} channelId
 * @returns {Matcher} channel matcher
 */
const lastMessageChannelIs = (channelId) => (conversation) =>
  conversation.last_message_channel === channelId;

/**
 * A Matcher that returns true if the conversation is unassigned
 * @param {Conversation} conversation
 * @returns {Boolean} result
 */
const isUnassigned = (conversation) =>
  conversation.all_assigned_agent_ids.length === 0;

/**
 * Takes a contact id and returns a Matcher which returns true if the conversation is with the contact
 * @param {Number} contactId
 * @returns {Matcher} contact matcher
 */
const conversationIsWith = (contactId) => (conversation) =>
  conversation.user_id === contactId;

/**
 * Takes a tag id and returns a Matcher which returns true if the conversation has the tag assigned
 * @param {Number} tagId
 * @returns {Matcher} tag matcher
 */
const conversationHasTag = (tagId) => (conversation) =>
  conversation.tag_ids?.includes(tagId);

/**
 * Takes a conversation and returns whether or not it is waiting for an agent to respond
 * @param {Conversation} conversation
 * @returns {Boolean}
 */
const isAwaitingAgentResponse = (conversation) => {
  return conversation.awaiting_agent_response === true;
};

/**
 * Takes a conversation and returns whether or not it has a team directly assigned
 * @param {Conversation} conversation
 * @returns {Boolean}
 */
const isAssignedDirectlyToAnyTeam = (conversation) => {
  return conversation.assigned_team_ids.length !== 0;
};

/**
 * Takes a conversation and returns whether or not it has an agent directly assigned
 * @param {Conversation} conversation
 * @returns {Boolean}
 */
const isAssignedDirectlyToAnyAgent = (conversation) => {
  return conversation.assigned_agent_ids.length !== 0;
};

/**
 * Takes a conversation and returns whether or not it assigned to a team but no agents
 * @param {Conversation} conversation
 * @returns {Boolean}
 */
const isAssignedToTeamButNoAgents = all([
  isAssignedDirectlyToAnyTeam,
  negate(isAssignedDirectlyToAnyAgent),
]);

/**
 * Takes a timestamp and returns a Matcher which returns true if the conversation was started before that timestamp
 * @param {String} timestamp
 * @returns {Matcher} timestamp before matcher
 */
const conversationStartedBefore = (timestamp) => (conversation) => {
  const conversationStarted = new Date(conversation.createdAt).getTime() / 1000;
  return conversationStarted <= Number(timestamp);
};

/**
 * Takes a timestamp and returns a Matcher which returns true if the conversation was started after that timestamp
 * @param {String} timestamp
 * @returns {Matcher} timestamp after matcher
 */
const conversationStartedAfter = (timestamp) => (conversation) => {
  const conversationStarted = new Date(conversation.createdAt).getTime() / 1000;
  return conversationStarted >= Number(timestamp);
};

/**
 * Takes a timestamp and returns a Matcher which returns true if the conversation was started at least a day after that timestamp
 * @param {String} timestamp
 * @returns {Matcher} timestamp after day matcher
 */
const conversationStartedAfterDay = (timestamp) => {
  const endOfDay = Number(timestamp) + 86400;
  return conversationStartedAfter(endOfDay);
};

/**
 * Takes a pair of timestamps and returns a Matcher which returns true if the conversation was started between those 2 timestamps
 * @param {String} startTimestamp
 * @param {String} endTimestamp
 * @returns {Matcher} timestamp between matcher
 */
const conversationStartedBetween = (startTimestamp, endTimestamp) => {
  return all([
    conversationStartedAfter(startTimestamp),
    conversationStartedBefore(endTimestamp),
  ]);
};

/**
 * Takes a timestamp and returns a Matcher which returns true if the conversation was started on the day of the timestamp
 * @param {String} timestamp
 * @returns {Matcher} on day matcher
 */
const conversationStartedOn = (timestamp) => {
  const startOfDay = Number(timestamp);
  const endOfDay = startOfDay + 86400;
  return conversationStartedBetween(startOfDay, endOfDay);
};

/**
 * A matcher which returns true if the conversation has any failed messages in it
 */
const conversationHasFailedMessages = (conversation) =>
  conversation.has_failed_messages === true;

/**
 * A matcher which returns true if the conversation has not been ignored
 */
const conversationIsNotIgnored = (conversation) =>
  conversation.ignored === false;

/**
 * Takes a status filter and returns a Matcher which returns true if the conversation matches the filter
 * @param {*} statusFilter
 * @returns {Matcher} filter matcher
 */
const matchesStatusFilter = (statusFilter) => {
  const { operator, value } = statusFilter;
  switch (operator) {
    case 'choice:is':
      return friendlyStatusIsOneOf(value);
    case 'choice:is not':
      return negate(friendlyStatusIsOneOf(value));
  }
  throw new Error(`Unknown custom filter ${JSON.stringify(statusFilter)}`);
};

/**
 * Takes a source filter and returns a Matcher which returns true if the conversation matches the filter
 * @param {*} sourceFilter
 * @returns {Matcher} filter matcher
 */
const matchesSourceFilter = (sourceFilter) => {
  const { operator, value } = sourceFilter;
  switch (operator) {
    case 'choice:is':
      return (conversation) => {
        return value.some((v) => conversation.message_sources.includes(v));
      };
    case 'choice:is not':
      return (conversation) => {
        return !value.some((v) => conversation.message_sources.includes(v));
      };
  }
  throw new Error(`Unknown custom filter ${JSON.stringify(sourceFilter)}`);
};

/**
 * Takes an agent filter and returns a Matcher which returns true if the conversation matches the filter
 * @param {*} agentFilter
 * @returns {Matcher} filter matcher
 */
const matchesAgentFilter = (agentFilter) => {
  const { operator, value } = agentFilter;
  switch (operator) {
    case 'custom:agent is':
      return either(
        value.map((agentId) => isAssignedDirectlyToAgent(Number(agentId)))
      );
    case 'custom:agent is not':
      return negate(
        either(
          value.map((agentId) => isAssignedDirectlyToAgent(Number(agentId)))
        )
      );
  }
  throw new Error(`Unknown custom filter ${JSON.stringify(agentFilter)}`);
};

/**
 * Takes a team filter and returns a Matcher which returns true if the conversation matches the filter
 * @param {*} teamFilter
 * @returns {Matcher} filter matcher
 */
const matchesTeamFilter = (teamFilter) => {
  const { operator, value } = teamFilter;
  switch (operator) {
    case 'custom:team is':
      return either(
        value.map((teamId) => isAssignedDirectlyToTeam(Number(teamId)))
      );
    case 'custom:team is not':
      return negate(
        either(value.map((teamId) => isAssignedDirectlyToTeam(Number(teamId))))
      );
  }
  throw new Error(`Unknown custom filter ${JSON.stringify(teamFilter)}`);
};

/**
 * Takes a bot filter and returns a Matcher which returns true if the conversation matches the filter
 * @param {*} botFilter
 * @returns {Matcher} filter matcher
 */
const matchesBotFilter = (botFilter) => {
  const { operator, value } = botFilter;
  switch (operator) {
    case 'custom:bot is':
      return either(value.map((botId) => isAssignedToBot(Number(botId))));
    case 'custom:bot is not':
      return negate(
        either(value.map((botId) => isAssignedToBot(Number(botId))))
      );
  }
  throw new Error(`Unknown custom filter ${JSON.stringify(botFilter)}`);
};

/**
 * Takes a last channel filter and returns a Matcher which returns true if the conversation matches the filter
 * @param {*} lastChannelFilter
 * @returns {Matcher} filter matcher
 */
const matchesLastChannelFilter = (lastChannelFilter) => {
  const { operator, value } = lastChannelFilter;
  switch (operator) {
    case 'custom:last channel is':
      return either(
        value.map((channelId) => lastMessageChannelIs(Number(channelId)))
      );
    case 'custom:last channel is not':
      return negate(
        either(
          value.map((channelId) => lastMessageChannelIs(Number(channelId)))
        )
      );
  }
  throw new Error(`Unknown custom filter ${JSON.stringify(lastChannelFilter)}`);
};

/**
 * Takes a tag filter and returns a Matcher which returns true if the conversation matches the filter
 * @param {*} tagFilter
 * @returns {Matcher} filter matcher
 */
const matchesTagFilter = (tagFilter) => {
  const { operator, value } = tagFilter;
  switch (operator) {
    case 'custom:tag is':
      return either(value.map((tagId) => conversationHasTag(Number(tagId))));
    case 'custom:tag is not':
      return negate(
        either(value.map((tagId) => conversationHasTag(Number(tagId))))
      );
  }
  throw new Error(`Unknown custom filter ${JSON.stringify(tagFilter)}`);
};

/**
 * Takes a started at filter and returns a Matcher which returns true if the conversation matches the filter
 * @param {*} startedAtFilter
 * @returns {Matcher} filter matcher
 */
const matchesStartedAtFilter = (startedAtFilter) => {
  const { operator, value } = startedAtFilter;
  switch (operator) {
    case 'date:is':
      return conversationStartedOn(value);
    case 'date:is before':
      return conversationStartedBefore(value);
    case 'date:is after':
      return conversationStartedAfterDay(value);
    case 'daterange:is between':
      return conversationStartedBetween(value.from, value.to);
  }
  throw new Error(`Unknown custom filter ${JSON.stringify(startedAtFilter)}`);
};

/**
 * Takes a filter and returns a Matcher which returns true if the conversation matches the filter
 * @param {*} filter
 * @returns {Matcher} filter matcher
 */
const matchesFilter = (filter) => {
  const { key } = filter;

  switch (key) {
    case 'status':
      return matchesStatusFilter(filter);
    case 'agent':
      return matchesAgentFilter(filter);
    case 'team':
      return matchesTeamFilter(filter);
    case 'bot':
      return matchesBotFilter(filter);
    case 'lastChannel':
      return matchesLastChannelFilter(filter);
    case 'tag':
      return matchesTagFilter(filter);
    case 'startedAt':
      return matchesStartedAtFilter(filter);
    case 'source':
      return matchesSourceFilter(filter);
  }

  throw new Error(`Unknown custom filter ${JSON.stringify(filter)}`);
};

/**
 * Takes the id of a logged in user and returns a Matcher that checks if the conversation should appear in their My Conversations filter
 * @param {Number} loggedInUserId
 * @returns {Matcher} "My Conversations" matcher
 */
export const myConversations = (loggedInUserId) =>
  all([
    isAssignedToAgent(loggedInUserId),
    statusIsNotOneOf(['no reply', 'channel switch', 'closed', 'merged']),
  ]);

/**
 * A matcher that checks if the conversation should appear in the Closed filter
 */
export const closedConversations = statusIs('closed');

/**
 * A matcher that checks if the conversation should appear in the Sent filter
 */
export const sentConversations = statusIsOneOf(['no reply', 'channel switch']);

/**
 * A matcher that checks if the conversation should appear in the Unassigned filter
 */
export const unassignedConversations = all([
  isUnassigned,
  statusIsNotOneOf(['no reply', 'channel switch', 'closed', 'merged']),
  isNotAssignedToAnyBot,
]);

/**
 * A matcher that checks if the conversation should appear in the Open filter
 */
export const openConversations = all([
  statusIsNotOneOf(['no reply', 'channel switch', 'closed', 'merged']),
  isNotAssignedToAnyBot,
]);

/**
 * A matcher that checks if the conversation should appear in the Assigned to Bot filter
 */
export const assignedToBotConversations = all([
  statusIsNotOneOf(['merged', 'closed']),
  hasBeenAssignedToAnyBot,
]);

/**
 * Takes a contact id and returns a Matcher that checks if the conversation should appear in the Contact Open filter
 * @param {Number} contactId
 * @returns {Matcher} "Contact" matcher
 */
export const contactConversations = (contactId) =>
  all([statusIsOneOf(['new', 'open']), conversationIsWith(contactId)]);

/**
 * Takes a contact id and returns a Matcher that checks if the conversation should appear in the Contact filter
 * @param {Number} contactId
 * @returns {Matcher} "Contact" matcher
 */
export const allForContactConversations = (contactId) =>
  all([statusIsNotOneOf(['merged']), conversationIsWith(contactId)]);

/**
 * Takes a tag id and returns a Matcher that checks if the conversation should appear in the Tag filter
 * @param {Number} tagId
 * @returns {Matcher} "Tag" matcher
 */
export const tagConversations = (tagId) =>
  all([statusIsNotOneOf(['merged']), conversationHasTag(tagId)]);

/**
 * A matcher that checks if the conversation should appear in the Undelivered filter
 */
export const undeliveredMessagesConversations = all([
  statusIsNotOneOf(['merged']),
  conversationHasFailedMessages,
  conversationIsNotIgnored,
]);

/**
 * Takes a an array of filters and returns a Matcher that checks if the conversation should appear in the Custom Filter filter
 * @param {*} filters
 * @returns {Matcher} "Custom Filter" matcher
 */
export const customFilterConversations = (filters) =>
  all([statusIsNotOneOf(['merged']), all(filters.map(matchesFilter))]);

/**
 * Takes a quick filter and returns a Matcher that checks if the conversation should be in it
 * @param {string|null} quickFilter
 * @param {Number} loggedInUserId
 * @returns {Matcher}
 */
export const matchesQuickFilter = (quickFilter) => {
  if (quickFilter === null) {
    return () => true;
  }
  const { type } = quickFilter;
  switch (type) {
    case QUICK_FILTERS.AWAITING_RESPONSE:
      return isAwaitingAgentResponse;
    case QUICK_FILTERS.TEAM_ASSIGNED_AWAITING_AGENT:
      return isAssignedToTeamButNoAgents;
    case QUICK_FILTERS.ASSIGNED_DIRECTLY_TO_AGENT:
      return isAssignedDirectlyToAgent(quickFilter.userId);
    case QUICK_FILTERS.ASSIGNED_TO_TEAM:
      return isAssignedDirectlyToTeam(quickFilter.teamId);
    default:
      return () => true;
  }
};

/**
 * Takes a Matcher and a quick filter and returns a Matcher that checks if the conversation should be in both
 * @param {Matcher} matcher
 * @param {string|null} quickFilter
 * @returns {Matcher}
 */
export const withQuickFilter = (matcher, quickFilter, moreOptions) => {
  return all([matcher, matchesQuickFilter(quickFilter, moreOptions)]);
};
